import React from 'react';
import { Card, Button, Space, message, Modal, Form, Input, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../constants';
import { ApplicationCell, DataTable, PageLayout } from '../../ui';

const PartnerApplicationsPage: React.FC = () => {
  const [data, setData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [current, setCurrent] = React.useState<any>(null);
  const [form] = Form.useForm();

  const token = React.useMemo(() => localStorage.getItem('token') || localStorage.getItem('authToken'), []);

  const fetchList = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/partner-applications`, {
        headers: token ? { Authorization: `Bearer ${token}` } as any : undefined,
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Ошибка загрузки');
      setData(json.data || []);
    } catch (e: any) {
      message.error(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { fetchList(); }, [fetchList]);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: 'Номер заявки',
      key: 'applicationNumber',
      render: (_: unknown, record: { id: number; applicationNumber?: string; applicationDate?: string | null }) => (
        <ApplicationCell
          withLink={false}
          application={{
            id: record.id,
            applicationNumber: record.applicationNumber ?? null,
            applicationDate: record.applicationDate ?? null
          }}
        />
      )
    },
    { title: 'Заказчик', dataIndex: 'customerName', key: 'customerName' },
    { title: 'Исполнитель', dataIndex: 'performerName', key: 'performerName' },
    { title: 'Грузоотправитель', dataIndex: 'shipperName', key: 'shipperName' },
    { title: 'Грузополучатель', dataIndex: 'consigneeName', key: 'consigneeName' },
    { title: 'Ставка', dataIndex: 'rate', key: 'rate', width: 120 },
    {
      title: 'Действия', key: 'actions', width: 220,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>Редактировать</Button>
          <Button size="small" onClick={() => onExport(record.id)}>DOCX</Button>
          <Button danger size="small" onClick={() => onDelete(record.id)}>Удалить</Button>
        </Space>
      )
    }
  ];

  const onEdit = (row: any) => {
    setCurrent(row);
    form.setFieldsValue({
      applicationDate: row.applicationDate ? dayjs(row.applicationDate) : undefined,
      applicationNumber: row.applicationNumber,
      customerName: row.customerName,
      performerName: row.performerName,
      shipperName: row.shipperName,
      consigneeName: row.consigneeName,
      loadingAddress: row.loadingAddress,
      unloadingAddress: row.unloadingAddress,
      contactPersonLoading: row.contactPersonLoading,
      contactPhoneLoading: row.contactPhoneLoading,
      contactPersonUnloading: row.contactPersonUnloading,
      contactPhoneUnloading: row.contactPhoneUnloading,
      rate: row.rate,
    });
    setEditOpen(true);
  };

  const onExport = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/partner-applications/${id}/docx`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } as any : undefined,
        credentials: 'include',
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error || 'Ошибка экспорта DOCX');
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const filename = m ? decodeURIComponent(m[1]) : `Заявка_${id}.docx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success('Файл сформирован');
    } catch (e: any) {
      message.error(e.message || 'Ошибка экспорта');
    }
  };

  const onDelete = async (id: number) => {
    Modal.confirm({
      title: 'Удалить заявку?',
      onOk: async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/partner-applications/${id}`, {
            method: 'DELETE',
            headers: token ? { Authorization: `Bearer ${token}` } as any : undefined,
            credentials: 'include'
          });
          const json = await res.json();
          if (!res.ok || !json.success) throw new Error(json.error || 'Ошибка удаления');
          message.success('Удалено');
          fetchList();
        } catch (e: any) { message.error(e.message || 'Ошибка удаления'); }
      }
    });
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const body: any = {
        applicationDate: values.applicationDate ? values.applicationDate.format('YYYY-MM-DD') : undefined,
        applicationNumber: values.applicationNumber,
        customerName: values.customerName,
        performerName: values.performerName,
        shipperName: values.shipperName,
        consigneeName: values.consigneeName,
        loadingAddress: values.loadingAddress,
        unloadingAddress: values.unloadingAddress,
        contactPersonLoading: values.contactPersonLoading,
        contactPhoneLoading: values.contactPhoneLoading,
        contactPersonUnloading: values.contactPersonUnloading,
        contactPhoneUnloading: values.contactPhoneUnloading,
        rate: values.rate ? Number(values.rate) : null,
      };
      const res = await fetch(`${API_BASE_URL}/partner-applications/${current.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        } as any,
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Ошибка сохранения');
      message.success('Сохранено');
      setEditOpen(false);
      fetchList();
    } catch (e: any) {
      message.error(e.message || 'Ошибка сохранения');
    }
  };

  return (
    <PageLayout
      title="Заявки партнёра"
      subtitle="Входящие заявки от партнёров"
      actions={<Button onClick={fetchList}>Обновить</Button>}
    >
      <Card>
        <DataTable rowKey="id" dataSource={data} columns={columns as any} loading={loading} pagination={{ pageSize: 20 }} />
      </Card>

      <Modal open={editOpen} onCancel={() => setEditOpen(false)} onOk={onSave} okText="Сохранить" title={`Заявка #${current?.id || ''}`}>
        <Form form={form} layout="vertical">
          <Form.Item name="applicationDate" label="Дата">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="applicationNumber" label="Номер">
            <Input />
          </Form.Item>
          <Form.Item name="customerName" label="Заказчик">
            <Input />
          </Form.Item>
          <Form.Item name="performerName" label="Исполнитель">
            <Input />
          </Form.Item>
          <Form.Item name="shipperName" label="Грузоотправитель">
            <Input />
          </Form.Item>
          <Form.Item name="consigneeName" label="Грузополучатель">
            <Input />
          </Form.Item>
          <Form.Item name="loadingAddress" label="Адрес погрузки">
            <Input />
          </Form.Item>
          <Form.Item name="unloadingAddress" label="Адрес выгрузки">
            <Input />
          </Form.Item>
          <Form.Item name="contactPersonLoading" label="Контакт (загрузка)">
            <Input />
          </Form.Item>
          <Form.Item name="contactPhoneLoading" label="Телефон (загрузка)">
            <Input />
          </Form.Item>
          <Form.Item name="contactPersonUnloading" label="Контакт (выгрузка)">
            <Input />
          </Form.Item>
          <Form.Item name="contactPhoneUnloading" label="Телефон (выгрузка)">
            <Input />
          </Form.Item>
          <Form.Item name="rate" label="Ставка, руб.">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
};

export default PartnerApplicationsPage;


